import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Space, message, Tag, Modal, Form, Tooltip, Popconfirm, Switch,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall, makePutCall, makeDeleteCall } from '../../utils/helper';
import './masters-config.css';

const ReferralPhysicians = () => {
  const [physiciansList, setPhysiciansList] = useState({ data: [], loading: false, error: null });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [searchText, setSearchText] = useState('');

  const [form] = Form.useForm();

  const fetchPhysicians = useCallback(async () => {
    setPhysiciansList(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await makeGetCall('/referral-physicians');
      if (response?.data?.success) {
        setPhysiciansList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch referral physicians');
      }
    } catch (error) {
      setPhysiciansList({ data: [], loading: false, error: error.message || 'Failed to fetch referral physicians' });
      message.error('Failed to fetch referral physicians');
    }
  }, []);

  useEffect(() => {
    fetchPhysicians();
  }, [fetchPhysicians]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      rp_doc_code: record.rp_doc_code,
      rp_name: record.rp_name,
      rp_email: record.rp_email,
      rp_contact_number: record.rp_contact_number,
      rp_department_code: record.rp_department_code,
      // Reflect the current active status in the toggle (defaults to active
      // if the record has no explicit value).
      rp_is_active: record.rp_is_active !== 'N',
    });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    setDeletingId(record.rp_id);
    try {
      const res = await makeDeleteCall(`/referral-physicians/${record.rp_id}?permanent=true`);
      if (res?.data?.success) {
        message.success('Referral physician deleted');
        fetchPhysicians();
      } else {
        throw new Error(res?.data?.message || 'Failed to delete referral physician');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete referral physician');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      rp_doc_code: String(values.rp_doc_code || '').trim(),
      rp_name: String(values.rp_name || '').trim(),
      rp_email: values.rp_email ? String(values.rp_email).trim() : null,
      rp_contact_number: values.rp_contact_number ? String(values.rp_contact_number).trim() : null,
      rp_department_code: values.rp_department_code ? String(values.rp_department_code).trim() : null,
      // Only send the active flag when editing — the create endpoint always
      // makes a new physician active and ignores this field.
      ...(editingRecord ? { rp_is_active: values.rp_is_active ? 'Y' : 'N' } : {}),
    };

    setSubmitting(true);
    try {
      const res = editingRecord
        ? await makePutCall(`/referral-physicians/${editingRecord.rp_id}`, payload)
        : await makePostCall('/referral-physicians', payload);

      if (res?.data?.success) {
        message.success(`Referral physician ${editingRecord ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchPhysicians();
      } else {
        throw new Error(res?.data?.message || 'Failed to save referral physician');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to save referral physician');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
  };

  const filteredPhysicians = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (physiciansList.data || []).filter(item => {
      if (!search) return true;
      const haystack = `${item.rp_name || ''} ${item.rp_doc_code || ''} ${item.rp_email || ''} ${item.rp_contact_number || ''} ${item.rp_department_code || ''}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [physiciansList.data, searchText]);

  const columns = [
    {
      title: 'Physician Code',
      dataIndex: 'rp_doc_code',
      key: 'rp_doc_code',
      sorter: (a, b) => (a.rp_doc_code || '').localeCompare(b.rp_doc_code || ''),
      render: (code) => <Tag>{code}</Tag>,
    },
    {
      title: 'Full Name',
      dataIndex: 'rp_name',
      key: 'rp_name',
      sorter: (a, b) => (a.rp_name || '').localeCompare(b.rp_name || ''),
    },
    {
      title: 'Email',
      dataIndex: 'rp_email',
      key: 'rp_email',
      render: (email) => email || <span className="mc-muted">—</span>,
    },
    {
      title: 'Phone Number',
      dataIndex: 'rp_contact_number',
      key: 'rp_contact_number',
      render: (phone) => phone || <span className="mc-muted">—</span>,
    },
    {
      title: 'Department Code',
      dataIndex: 'rp_department_code',
      key: 'rp_department_code',
      render: (dept) => (dept ? <Tag color="blue">{dept}</Tag> : <span className="mc-muted">—</span>),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Physician">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete this referral physician?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} loading={deletingId === record.rp_id}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Referral Physician</span>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Physician
        </Button>
      </div>

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by code, name, email, phone or department"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 340 }}
            allowClear
          />
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {physiciansList.error && (
        <div className="mc-error">
          <p>{physiciansList.error}</p>
          <Button onClick={fetchPhysicians}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Referral Physicians</span>
          <span className="mc-count">{filteredPhysicians.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredPhysicians}
            loading={physiciansList.loading}
            rowKey="rp_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} physicians`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit Referral Physician' : 'Add Referral Physician'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Physician Code"
            name="rp_doc_code"
            rules={[
              { required: true, message: 'Please enter the physician code' },
              { max: 50, message: 'Code must be 50 characters or less' },
            ]}
          >
            <Input placeholder="e.g. DOC001" />
          </Form.Item>

          <Form.Item
            label="Full Name"
            name="rp_name"
            rules={[
              { required: true, message: 'Please enter the full name' },
              { max: 255, message: 'Name must be 255 characters or less' },
            ]}
          >
            <Input placeholder="e.g. Dr. Rajesh Sharma" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="rp_email"
            rules={[
              { type: 'email', message: 'Please enter a valid email address' },
              { max: 100, message: 'Email must be 100 characters or less' },
            ]}
          >
            <Input placeholder="e.g. doctor@hospital.com (optional)" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="rp_contact_number"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const digits = String(value).replace(/\D/g, '');
                  return digits.length === 10
                    ? Promise.resolve()
                    : Promise.reject(new Error('Phone number should be 10 digits'));
                },
              },
            ]}
          >
            <Input placeholder="e.g. 9876543210 (optional)" />
          </Form.Item>

          <Form.Item
            label="Department Code"
            name="rp_department_code"
            rules={[{ max: 50, message: 'Department code must be 50 characters or less' }]}
          >
            <Input placeholder="e.g. CT (optional)" />
          </Form.Item>

          {/* Active-status toggle — only meaningful when editing an existing
              physician (create always starts active). Maps to rp_is_active. */}
          {editingRecord && (
            <Form.Item
              label="Active Status"
              name="rp_is_active"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <Form.Item className="form-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReferralPhysicians;
